import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Skeleton } from '@mui/material';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceAppVersionFilesListItem, DeviceAppVersionFilesListProps } from './deviceAppVersionFilesList.model';
import styles from './deviceAppVersionFilesList.module.scss';

export function DeviceAppVersionFilesList({
  items,
  onDelete,
  readOnly,
  isLoading,
}: DeviceAppVersionFilesListProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.APPLICATION]);

  const handleRemoveItem = useCallback((item: DeviceAppVersionFilesListItem) => () => onDelete?.(item), [onDelete]);

  const getListItem = useCallback(
    ({ filename, fileInfo, unistalledApps }: DeviceAppVersionFilesListItem, idx: number) => {
      return (
        <div
          className={classNames({ [styles.listItem__last]: idx === items.length - 1 }, styles.listItem)}
          key={filename}
        >
          <div className={styles.listItem__row}>
            <div className={styles.listItem__labelContainer}>
              <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{filename}</CoreTypography>
              {fileInfo ? (
                <div className={styles.listItem__fileInfoContainer}>
                  <InsertDriveFileIcon />
                  <CoreTypography variant={CoreTypographyVariant.Body03Medium}>{fileInfo}</CoreTypography>
                </div>
              ) : undefined}
            </div>
            {readOnly ? undefined : (
              <CoreButton
                className={styles.listItem__removeButton}
                onClick={handleRemoveItem({ filename, fileInfo })}
                variant="text"
              >
                <CloseIcon />
              </CoreButton>
            )}
          </div>
          {unistalledApps?.length && (
            <div className={styles.listItem__uninstalledApps__container}>
              <CoreTypography
                variant={CoreTypographyVariant.Body02Regular}
                customClassName={styles.listItem__uninstalledApps__title}
              >
                {t('title.uninstallApplication', { ns: ALL_LNGS_NAMESPACES.APPLICATION })}
              </CoreTypography>
              {unistalledApps.map((app) => (
                <CoreTypography key={app} variant={CoreTypographyVariant.Body02Regular}>
                  {app}
                </CoreTypography>
              ))}
            </div>
          )}
        </div>
      );
    },
    [items.length, readOnly, handleRemoveItem, t],
  );

  const getSkeleton = useCallback(
    (rowsSize: number) => (
      <div className={styles.skeleton__container}>
        {Array.from({ length: rowsSize }, (_, idx) => idx).map((key) => (
          <div className={styles.skeleton__wrapper} key={key}>
            <Skeleton height={16} width={'30%'} />
            <Skeleton height={16} width={'70%'} />
          </div>
        ))}
      </div>
    ),
    [],
  );

  return (
    <div className={styles.container}>
      {isLoading ? getSkeleton(items.length) : items.map((item, idx) => getListItem(item, idx))}
    </div>
  );
}
